import type { Middleware } from 'openapi-fetch';
import { GESTURE_HEADER, applyInTurn, type UndoStack, type UndoStep, type WriteScope } from './UndoStack';

// Every persistent editor edit is a v1 write; this records each one on the undo stack as it happens.
// A write carrying UNTRACKED_HEADER is not an edit of the user's (undo and redo themselves, the
// renderer placing what the editor just added) and is sent without it, unrecorded. A write carrying
// GESTURE_HEADER joins that gesture's step; any other write is a step of its own. Scene content is
// always recorded; scene, story and library writes only inside a gesture, since the editor also makes
// them on its own (anchor category, cover screenshots, story attribution, durations).

export const UNTRACKED_HEADER = 'X-Lureo-Untracked';

/** The scope of a v1 write the undo stack must not record. */
export const untracked: WriteScope = { headers: { [UNTRACKED_HEADER]: '1' } };

type Row = Record<string, unknown>;

/** A scene, story or library row the editor changes field by field. */
export interface FieldPort {
  /** The saved row. */
  get(id: string): Promise<Row>;
  /** Writes these fields (untracked), tells the editor (and the renderer), and returns the saved row. */
  patch(id: string, fields: Row): Promise<Row>;
}

/** How the undo stack reads, rewrites and deletes one kind of scene content. */
export interface ScenePort extends FieldPort {
  /**
   * Soft-deletes it (untracked), takes it and what went with it out of the renderer and the editor, and resolves
   * the deletion's id for restoreDeletion; null when it was gone already.
   */
  remove(id: string): Promise<string | null>;
}

/** How the undo of a change from elsewhere rewrites and deletes an effect. */
export type VfxPort = Omit<ScenePort, 'get'>;

/** Brings back what a delete hid. */
export interface DeletionPort {
  /** Restores the deletion (restoreDeletion) and shows every row it brought back to the renderer and the editor. */
  restore(deletionId: string): Promise<void>;
}

/** Materials are kept per node of an asset instance, not by id: a material put back by its look is a new row. */
export interface MaterialPort {
  /** The materials on these asset instances. */
  listFor(assetInstanceIds: string[]): Promise<Row[]>;
  /** The materials in this scene. */
  listScene(sceneId: string): Promise<Row[]>;
  /** Puts this row's material back on its node (untracked) and tells the renderer and the editor. */
  restore(row: Row): Promise<void>;
  /** Clears its node (untracked) and tells the renderer and the editor. */
  clear(row: Row): Promise<void>;
  /** Soft-deletes this material (untracked) and tells the renderer and the editor; resolves the deletion's id. */
  remove(row: Row): Promise<string | null>;
  /** The asset instance a material row is on, as the editor knows it; undefined when it does not. */
  assetOf?(materialId: string): string | undefined;
}

/** Keeps what the editor knows of the open scene's rows as this tab's own writes leave them. */
export interface KnownRowsPort {
  /** Notes the rows a write of the open scene saved or deleted, from what it sent and what it answered. */
  saved(write: SceneWrite, body: unknown, answer: unknown): void;
}

/** Shows rows as the server has them after a write to them failed. */
export interface RereadPort {
  /** Reads the failed write's rows again and shows them to the renderer and the editor, while its scene is open. */
  failedWrite(failure: FailedWrite): Promise<void>;
}

export interface ScenePorts {
  asset: ScenePort;
  keyframe: ScenePort;
  clip: ScenePort;
  uiElement: ScenePort;
  material: MaterialPort;
  scene: FieldPort;
  story: FieldPort;
  assetType: ScenePort;
  uiElementType: FieldPort;
  materialType: ScenePort;
  vfx: VfxPort;
  deletions: DeletionPort;
  reread: RereadPort;
  known: KnownRowsPort;
}

/** The kinds this tab writes; effects only as undo puts back a change from elsewhere, so their writes are watched but never recorded. */
export type AnyKind = Exclude<keyof ScenePorts, 'deletions' | 'reread' | 'known'>;
type FieldKind = 'scene' | 'story' | 'assetType' | 'uiElementType';
type RowKind = Exclude<AnyKind, FieldKind | 'material' | 'vfx'> | 'assetType';

const COLLECTIONS: Record<string, AnyKind> = {
  'asset-instances': 'asset',
  keyframes: 'keyframe',
  'clip-instances': 'clip',
  'ui-element-instances': 'uiElement',
  'material-instances': 'material',
  scenes: 'scene',
  stories: 'story',
  'asset-types': 'assetType',
  'ui-element-types': 'uiElementType',
  'material-types': 'materialType',
  'vfx-instances': 'vfx',
};

// Recorded only inside a gesture; of these only material presets are created and deleted, the rest changed
const GESTURE_ONLY = new Set<AnyKind>(['scene', 'story', 'assetType', 'uiElementType', 'materialType']);
const FIELDS_ONLY = new Set<AnyKind>(['scene', 'story', 'assetType', 'uiElementType']);
const isFieldKind = (kind: AnyKind): kind is FieldKind => FIELDS_ONLY.has(kind);

const LABELS: Record<AnyKind, string> = {
  asset: 'Asset',
  keyframe: 'Keyframe',
  clip: 'Clip',
  uiElement: 'Screen message',
  material: 'Material',
  scene: 'Scene settings',
  story: 'Story',
  assetType: 'Library item',
  uiElementType: 'Screen message style',
  materialType: 'Material preset',
  vfx: 'Effect',
};

/** A v1 scene write: which row kind, and whether it creates, changes many, changes one or deletes. */
export type SceneWrite =
  | { kind: AnyKind; op: 'create' }
  | { kind: AnyKind; op: 'batch' }
  | { kind: AnyKind; op: 'patch'; id: string }
  | { kind: AnyKind; op: 'delete'; id: string };

/** A scene write that answered with an error, or with nothing, while a scene was open. */
export interface FailedWrite {
  write: SceneWrite;
  /** What it sent. */
  body: unknown;
  /** The rows it names, as `kind:id`: a create's from its body, materials (kept per node) as one key. */
  rows: string[];
  /** The status it answered with; 0 when no answer came. */
  status: number;
  /** The gesture it was part of. */
  gestureId?: string;
  /** Whether it was the user's edit (recorded, or in a gesture), not one the editor or the renderer made on its own or an undo's. */
  byUser: boolean;
  /** The scene open as it was sent. */
  sceneId: string;
  /** Resolves once no write to its rows is in flight. */
  settled(): Promise<void>;
}

/** The scene write a v1 request is, or null for any other request. `id` is the request's path parameter. */
export function sceneWriteOf(method: string, schemaPath: string, id: unknown): SceneWrite | null {
  const match = /^\/api\/v1\/([a-z-]+)(\/batch|\/\{[A-Za-z]+\})?$/.exec(schemaPath);
  const kind = match ? COLLECTIONS[match[1]] : undefined;
  if (!kind) return null;
  const one = !!match![2] && match![2] !== '/batch';
  // A library item is deleted (undoably, with its uses) as well as changed
  if (kind === 'assetType' && one && method === 'DELETE' && typeof id === 'string') return { kind, op: 'delete', id };
  if (isFieldKind(kind)) {
    return one && method === 'PATCH' && typeof id === 'string' ? { kind, op: 'patch', id } : null;
  }
  if (method === 'POST' && !match![2]) return { kind, op: 'create' };
  if (method === 'POST' && match![2] === '/batch') return kind === 'materialType' ? null : { kind, op: 'batch' };
  if (one && typeof id === 'string' && (method === 'PATCH' || method === 'DELETE')) {
    return { kind, op: method === 'PATCH' ? 'patch' : 'delete', id };
  }
  return null;
}

/** Whether a write of this kind is recorded only inside a gesture. A library item's delete is always the user's. */
export const gestureOnly = (write: SceneWrite) => GESTURE_ONLY.has(write.kind) && !(write.kind === 'assetType' && write.op === 'delete');

const pick = (row: Row, keys: string[]): Row => Object.fromEntries(keys.map(key => [key, row[key] ?? null]));

/** Whether `row` already holds `values`, key for key: a write of them changes nothing. */
const holds = (row: Row | undefined, values: Row): boolean =>
  !!row && Object.keys(values).every(key => JSON.stringify(row[key] ?? null) === JSON.stringify(values[key] ?? null));

// What a material on a node is, apart from its id and when it was written
const MATERIAL_VALUES = ['shaderLookUniqueId', 'materialTypeUniqueId', 'unityProperties', 'webProperties', 'webxrProperties', 'swiftProperties'];
const sameMaterial = (before: Row | undefined, after: Row): boolean => holds(before, pick(after, MATERIAL_VALUES));

const isObject = (value: unknown): value is Row => !!value && typeof value === 'object' && !Array.isArray(value);

/** The top-level keys whose values differ between two payloads. */
export function changedKeys(before: unknown, after: unknown): string[] {
  const was = isObject(before) ? before : {};
  const now = isObject(after) ? after : {};
  return [...new Set([...Object.keys(was), ...Object.keys(now)])].filter(key => JSON.stringify(was[key]) !== JSON.stringify(now[key]));
}

/** `current` with `keys` set as `source` has them, and removed where it has none. */
export function withKeys(current: unknown, source: unknown, keys: string[]): Row {
  const next: Row = { ...(isObject(current) ? current : {}) };
  const from = isObject(source) ? source : {};
  for (const key of keys) {
    if (key in from) next[key] = from[key];
    else delete next[key];
  }
  return next;
}

/** What a step holds while it can bring back a placement of this asset type, so the type is not deleted under it. */
export const assetTypeHold = (assetTypeId: string) => `assetType:${assetTypeId}`;

const typeHold = (row: unknown): string[] => {
  const id = (row as Row | undefined)?.assetTypeUniqueId;
  return typeof id === 'string' ? [assetTypeHold(id)] : [];
};

/** Whether a port call failed because its row is gone (404): deleted since, with the library item it placed, say. */
const isGone = (error: unknown) => (error as { status?: unknown } | null)?.status === 404;

/** Whether a read failed for a reason that passes on its own: the rate limit, or the server. */
const passes = (error: unknown) => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && (status === 429 || status >= 500);
};

/** How long a prior-state read waits before each try again after a failure that passes. */
const PRIOR_READ_RETRY_MS = [300, 1000];

/** `read`, tried again after each wait while it fails for a reason that passes. */
async function retrying<T>(read: () => Promise<T>): Promise<T> {
  for (const wait of PRIOR_READ_RETRY_MS) {
    try {
      return await read();
    } catch (error) {
      if (!passes(error)) throw error;
    }
    await new Promise(resolve => setTimeout(resolve, wait));
  }
  return read();
}

/** A deletion's resources (DeletedRowsDto) by the kind the undo stack names their rows with. */
const DELETED_KINDS: Record<string, string> = {
  assetInstances: 'asset',
  keyframes: 'keyframe',
  clipInstances: 'clip',
  uiElementInstances: 'uiElement',
  materialInstances: 'material',
  vfxInstances: 'vfx',
  assetTypes: 'assetType',
  materialTypes: 'materialType',
  scenes: 'scene',
  stories: 'story',
  images: 'image',
};

/** Every row a delete's answer (DeletionDto) says it hid, as `kind:id`. */
function deletedKeys(response: unknown): string[] {
  const deleted = ((response as Row | null)?.deleted ?? {}) as Record<string, unknown>;
  return Object.entries(deleted).flatMap(([resource, ids]) =>
    Array.isArray(ids) ? ids.map(id => `${DELETED_KINDS[resource] ?? resource}:${id}`) : []);
}

/** Runs `write` on a row, which is done already when the row is gone: there is nothing left to put back. */
async function unlessGone(write: () => Promise<unknown>): Promise<void> {
  try {
    await write();
  } catch (error) {
    if (!isGone(error)) throw error;
  }
}

const targetOf = (row: Row) => `${row.assetInstanceUniqueId}|${row.targetNodeName ?? ''}|${row.targetNodeIndex ?? ''}`;

type Recorder = { record(step: UndoStep): void };

/** What the write needs to know before it happens, so its undo can put things back. */
type Prior = { rows?: Row[] };

/** The prior state, and which opening of a scene it was read in. */
type Before = Prior & { epoch: number };

export interface SceneWriteRecorder {
  /** Turns recording on for an open scene, off (null) otherwise. A write begun before is never recorded. */
  setScene(sceneId: string | null): void;
  /** The scene open now, or null. */
  scene(): string | null;
  /** Reads what the write is about to change; resolves null when the write is not recorded. */
  before(write: SceneWrite, body: unknown): Promise<Before | null>;
  /** Records the finished write on the undo stack, in the step of the gesture it belongs to, if any. */
  record(write: SceneWrite, body: unknown, response: unknown, before: Before, gestureId?: string): void;
  /** Whether a write of this kind is recorded only inside a gesture. */
  gestureOnly(write: SceneWrite): boolean;
  /** Notes a tracked write in flight, of the gesture `gestureId` if given; the returned function marks it finished. */
  writeStarted(gestureId?: string): () => void;
  /** The ports its steps read and write through, for steps recorded elsewhere (changes from another client). */
  readonly ports: ScenePorts;
}

export function sceneWriteRecorder(undoStack: UndoStack, ports: ScenePorts): SceneWriteRecorder {
  let sceneId: string | null = null;
  // Bumped whenever the open scene changes, so a write begun in one opening is never recorded in another
  let epoch = 0;

  // Only the open scene's edits go on its stack (a draft trigger torn down as another scene opens, say)
  const inScene = (row: Row | undefined) => !row || row.sceneUniqueId === undefined || row.sceneUniqueId === sceneId;

  async function before(write: SceneWrite, body: unknown): Promise<Before | null> {
    const at = epoch;
    // The write waits for this read, so a rate limit or a server error is waited out rather than leaving it unrecorded
    const prior = await retrying(() => readPrior(write, body));
    return prior && at === epoch ? { ...prior, epoch: at } : null;
  }

  async function readPrior(write: SceneWrite, body: unknown): Promise<Prior | null> {
    if (!sceneId) return null;
    // A library item belongs to no scene; deleting it while a scene is open is that scene's edit
    if (write.kind === 'assetType' && write.op === 'delete') return {};
    if (isFieldKind(write.kind)) {
      if (write.op !== 'patch' || (write.kind === 'scene' && write.id !== sceneId)) return null;
      return { rows: [await ports[write.kind].get(write.id)] };
    }
    if (write.kind === 'material') {
      if (write.op === 'create') return null;
      if (write.op === 'batch') {
        const items = ((body as { items?: Row[] })?.items ?? []);
        return { rows: await ports.material.listFor([...new Set(items.map(item => String(item.assetInstanceUniqueId)))]) };
      }
      // v1 reads materials by scene or asset only: by its asset when the editor knows which
      const id = write.id;
      const assetId = ports.material.assetOf?.(id);
      const listed = assetId ? await ports.material.listFor([assetId]) : await ports.material.listScene(sceneId);
      const row = listed.find(material => material.uniqueId === id);
      return row ? { rows: [row] } : null;
    }
    const port = ports[write.kind as RowKind];
    switch (write.op) {
      case 'patch':
      case 'delete': {
        const row = await port.get(write.id);
        return inScene(row) ? { rows: [row] } : null;
      }
      case 'batch': {
        const items = ((body as { items?: Row[] })?.items ?? []);
        const rows = await Promise.all(items.map(item => port.get(String(item.uniqueId))));
        return rows.every(inScene) ? { rows } : null;
      }
      case 'create':
        return inScene(body as Row | undefined) ? {} : null;
    }
  }

  function record(write: SceneWrite, body: unknown, response: unknown, prior: Before, gestureId?: string): void {
    // Its scene closed (another opened) before it answered: that history is gone, and what it deleted with it
    if (prior.epoch !== epoch) {
      const deletionId = write.op === 'delete' ? (response as Row | null)?.deletionUniqueId : undefined;
      if (typeof deletionId === 'string') undoStack.purges$.next([deletionId]);
      return;
    }
    const label = LABELS[write.kind];
    const stack = { record: (step: UndoStep) => undoStack.record(step, gestureId) };
    const rows = [...rowKeysOf(write, body, ports.material), ...createdKeys(write, response), ...deletedKeys(response)];
    if (write.kind === 'material') {
      recordMaterial(stack, write, label, body, response, prior, rows);
      return;
    }
    if (write.op === 'patch') {
      const port: FieldPort = ports[write.kind as RowKind | FieldKind];
      const fields = body as Row;
      // A release or blur that leaves the row as it was (a slider let go where it started) is no step
      if (holds(prior.rows![0], fields)) return;
      const old = pick(prior.rows![0], Object.keys(fields));
      // A scene's payload holds settings other writes change too (publishing's target devices): only the
      // keys this write changed go back and forth, onto the payload as it is then
      const payloadKeys = write.kind === 'scene' && isObject(fields.payload) ? changedKeys(old.payload, fields.payload) : null;
      const apply = (values: Row) => () => unlessGone(async () => {
        if (!payloadKeys) return void (await port.patch(write.id, values));
        const current = (await port.get(write.id)).payload;
        await port.patch(write.id, { ...values, payload: withKeys(current, values.payload, payloadKeys) });
      });
      stack.record({
        label,
        rows,
        mergeKey: `${write.kind}:${write.id}:${Object.keys(fields).sort().join(',')}`,
        undo: apply(old),
        redo: apply(fields),
        // Undo points the placement back at the type it had
        ...(write.kind === 'asset' && 'assetTypeUniqueId' in fields ? { holds: typeHold(old) } : {}),
      });
      return;
    }
    const port = ports[write.kind as RowKind];
    switch (write.op) {
      case 'batch': {
        const items = ((body as { items?: Row[] }).items ?? []);
        const olds = items.map((item, i) => pick(prior.rows![i], Object.keys(item).filter(key => key !== 'uniqueId')));
        const news = items.map(item => Object.fromEntries(Object.entries(item).filter(([key]) => key !== 'uniqueId')));
        // A gizmo released where it was grabbed moves nothing
        if (news.every((values, i) => holds(prior.rows![i], values))) return;
        // Row after row; a row gone since is skipped, and any other failure puts back the rows already
        // written, so the step is never left half applied
        const apply = (values: Row[], back: Row[]) => () => applyInTurn(
          [...items.keys()],
          i => unlessGone(() => port.patch(String(items[i].uniqueId), values[i])),
          i => unlessGone(() => port.patch(String(items[i].uniqueId), back[i])),
        );
        stack.record({
          label,
          rows,
          undo: apply(olds, news),
          redo: apply(news, olds),
        });
        return;
      }
      case 'create': {
        // A placement answers with its asset instance and what was created with it
        const created = response as Row;
        const id = String((created.assetInstance as Row | undefined)?.uniqueId ?? created.uniqueId);
        // Undo soft-deletes it, and redo restores that deletion, with whatever it gained since
        let deletionId: string | null = null;
        stack.record({
          label,
          rows,
          holds: write.kind === 'asset' ? typeHold(created.assetInstance ?? created) : [],
          deletions: () => (deletionId ? [deletionId] : []),
          undo: async () => {
            deletionId = await port.remove(id);
          },
          redo: async () => {
            // Gone already when it was undone (deleted with its library item, say): nothing to bring back
            if (deletionId) await ports.deletions.restore(deletionId);
            deletionId = null;
          },
        });
        return;
      }
      case 'delete': {
        // Undo restores the deletion; redo deletes again, which makes a new one
        let deletionId = String((response as Row).deletionUniqueId);
        stack.record({
          label,
          rows,
          holds: typeHold(prior.rows?.[0]),
          deletions: () => [deletionId],
          undo: () => ports.deletions.restore(deletionId),
          redo: async () => {
            deletionId = (await port.remove(write.id)) ?? deletionId;
          },
        });
        return;
      }
    }
  }

  function recordMaterial(stack: Recorder, write: SceneWrite, label: string, body: unknown, response: unknown, prior: Before, rows: string[]): void {
    const { material } = ports;
    if (write.op === 'batch') {
      const written = (response as Row[]) ?? [];
      const beforeByTarget = new Map((prior.rows ?? []).map(row => [targetOf(row), row]));
      // Every node as it was (a slider let go where it started) is no step
      if (written.every(row => sameMaterial(beforeByTarget.get(targetOf(row)), row))) return;
      // Node after node, put back as a batch is when one fails; a node whose placement is gone is skipped
      const toOld = (row: Row) => unlessGone(() => {
        const old = beforeByTarget.get(targetOf(row));
        return old ? material.restore(old) : material.clear(row);
      });
      const toNew = (row: Row) => unlessGone(() => material.restore(row));
      stack.record({
        label,
        rows: [...rows, ...idsOf(written).map(id => `material:${id}`), ...idsOf(prior.rows).map(id => `material:${id}`)],
        undo: () => applyInTurn(written, toOld, toNew),
        redo: () => applyInTurn(written, toNew, toOld),
      });
      return;
    }
    const old = prior.rows![0];
    const keys = [...rows, `material:${old.uniqueId}`];
    if (write.op === 'patch') {
      const after = response as Row;
      if (sameMaterial(old, after)) return;
      stack.record({ label, rows: keys, undo: () => material.restore(old), redo: () => material.restore(after) });
    } else {
      let deletionId = String((response as Row).deletionUniqueId);
      stack.record({
        label,
        rows: keys,
        deletions: () => [deletionId],
        undo: () => ports.deletions.restore(deletionId),
        redo: async () => {
          deletionId = (await material.remove(old)) ?? deletionId;
        },
      });
    }
  }


  return {
    setScene: id => {
      sceneId = id;
      epoch += 1;
    },
    scene: () => sceneId,
    gestureOnly,
    writeStarted: gestureId => undoStack.writeStarted(gestureId),
    ports,
    before,
    record,
  };
}

const idsOf = (rows: unknown) => (Array.isArray(rows) ? rows.map(row => (row as Row)?.uniqueId).filter((id): id is string => typeof id === 'string') : []);

/** What a material write names: its asset instances' materials, or every material when the editor does not know its asset. */
export const ANY_MATERIAL = 'material';

/** How a write names the materials on an asset instance. */
export const materialsOf = (assetInstanceId: string) => `materials:${assetInstanceId}`;

/** The rows a write changes, as `kind:id`; materials, kept per node, by the asset instances they are on. */
function rowKeysOf(write: SceneWrite, body: unknown, materials: Pick<MaterialPort, 'assetOf'>): string[] {
  const row = (body ?? {}) as Row;
  if (write.kind === 'material') {
    if (write.op === 'patch' || write.op === 'delete') {
      const assetId = materials.assetOf?.(write.id);
      return [assetId ? materialsOf(assetId) : ANY_MATERIAL];
    }
    const items = Array.isArray(row.items) ? (row.items as Row[]) : [row];
    const assets = [...new Set(items.map(item => item?.assetInstanceUniqueId).filter((id): id is string => typeof id === 'string'))];
    return assets.length > 0 ? assets.map(materialsOf) : [ANY_MATERIAL];
  }
  switch (write.op) {
    case 'patch':
    case 'delete':
      return [`${write.kind}:${write.id}`];
    case 'batch':
      return idsOf(row.items).map(id => `${write.kind}:${id}`);
    case 'create':
      return [
        ...(typeof row.uniqueId === 'string' ? [`${write.kind}:${row.uniqueId}`] : []),
        // A placement creates its keyframes and clips with it
        ...idsOf(row.keyframes).map(id => `keyframe:${id}`),
        ...idsOf(row.clipInstances).map(id => `clip:${id}`),
      ];
  }
}

/** The rows a create answered with, as `kind:id`: a placement with its keyframes and clips. */
function createdKeys(write: SceneWrite, response: unknown): string[] {
  if (write.op !== 'create' || !isObject(response)) return [];
  const placed = response.assetInstance as Row | undefined;
  if (!placed) return typeof response.uniqueId === 'string' ? [`${write.kind}:${response.uniqueId}`] : [];
  return [
    ...idsOf([placed]).map(id => `asset:${id}`),
    ...idsOf(response.keyframes).map(id => `keyframe:${id}`),
    ...idsOf(response.clipInstances).map(id => `clip:${id}`),
  ];
}

/** Whether two writes' rows meet; a material write whose asset is not known meets every material write. */
function overlap(theirs: readonly string[], keys: readonly string[]): boolean {
  const anyMaterial = (rows: readonly string[]) => rows.includes(ANY_MATERIAL);
  const someMaterial = (rows: readonly string[]) => rows.some(row => row === ANY_MATERIAL || row.startsWith('materials:'));
  if ((anyMaterial(theirs) && someMaterial(keys)) || (anyMaterial(keys) && someMaterial(theirs))) return true;
  return theirs.some(key => keys.includes(key));
}

/**
 * A tracked write in flight: whether it deletes, the rows it changes once its body is read, and when it has
 * been recorded or failed.
 */
type Ticket = { deletes: boolean; keys: Promise<string[]>; done: Promise<void>; setKeys(keys: string[]): void; setDone(): void };

function ticket(deletes: boolean): Ticket {
  let setKeys!: (keys: string[]) => void;
  let setDone!: () => void;
  const keys = new Promise<string[]>(resolve => { setKeys = resolve; });
  const done = new Promise<void>(resolve => { setDone = resolve; });
  return { deletes, keys, done, setKeys, setDone };
}

/** One write in flight. */
export interface WriteInFlight {
  /** Names the rows it writes (`kind:id`), once its body is read; until then it may write any row. */
  names(rows: readonly string[]): void;
  /** Marks it answered. */
  end(): void;
}

/** This tab's writes in flight to the open scene, tracked or not, so a read of the scene can wait for them. */
export interface WritesInFlight {
  /** Notes a write as it starts, before it reaches the server. */
  begin(): WriteInFlight;
  /** How many writes have begun so far. */
  begun(): number;
  /** Resolves once no write in flight may write any of these rows; with none given, once no write is in flight. */
  idle(rows?: readonly string[]): Promise<void>;
  /** Resolves once idle(rows) holds, with how many writes have begun: a read started then predates every write to its rows unless touchedSince says otherwise. */
  quiet(rows?: readonly string[]): Promise<number>;
  /** Whether a write begun after the first `mark` may have written any of these rows: one naming one, or one naming none (a delete, or one not named yet); with none given, any write. */
  touchedSince(mark: number, rows?: readonly string[]): boolean;
}

export function writesInFlight(): WritesInFlight {
  const counts = new Map<string, number>();
  let unnamed = 0;
  let open = 0;
  let begun = 0;
  let waiting: Array<{ rows?: readonly string[]; resolve: () => void }> = [];
  // When the latest write naming each row began, the latest that ended naming none, and the ones not named yet
  const lastNamed = new Map<string, number>();
  let lastUnnamed = 0;
  const notNamed = new Set<number>();
  const touchedSince = (mark: number, rows?: readonly string[]) => {
    if (!rows) return begun > mark;
    if (lastUnnamed > mark || [...notNamed].some(at => at > mark)) return true;
    return rows.some(row => (lastNamed.get(row) ?? 0) > mark);
  };
  const busy = (rows?: readonly string[]) => (rows ? unnamed > 0 || rows.some(row => counts.has(row)) : open > 0);
  const wake = () => {
    const ready = waiting.filter(waiter => !busy(waiter.rows));
    waiting = waiting.filter(waiter => busy(waiter.rows));
    ready.forEach(waiter => waiter.resolve());
  };
  const idle = (rows?: readonly string[]): Promise<void> =>
    busy(rows) ? new Promise(resolve => waiting.push({ rows, resolve })) : Promise.resolve();
  return {
    begin() {
      begun += 1;
      open += 1;
      unnamed += 1;
      const at = begun;
      notNamed.add(at);
      let named: readonly string[] | null = null;
      let ended = false;
      return {
        names(rows) {
          if (named || ended) return;
          named = rows;
          unnamed -= 1;
          notNamed.delete(at);
          for (const row of rows) {
            counts.set(row, (counts.get(row) ?? 0) + 1);
            lastNamed.set(row, Math.max(lastNamed.get(row) ?? 0, at));
          }
          wake();
        },
        end() {
          if (ended) return;
          ended = true;
          open -= 1;
          if (!named) {
            unnamed -= 1;
            notNamed.delete(at);
            lastUnnamed = Math.max(lastUnnamed, at);
          }
          for (const row of named ?? []) {
            const left = (counts.get(row) ?? 1) - 1;
            if (left > 0) counts.set(row, left);
            else counts.delete(row);
          }
          wake();
        },
      };
    },
    begun: () => begun,
    idle,
    touchedSince,
    async quiet(rows) {
      // A write begun while it waited, just as the rows fell idle, may be on its way to them
      for (;;) {
        const before = begun;
        await idle(rows);
        if (!touchedSince(before, rows)) return before;
      }
    },
  };
}

/** The editor's writes in flight, as its v1 client's middleware notes them. */
export const editorWrites = writesInFlight();

/** Resolves once none of this tab's writes to an asset instance's materials is in flight. */
export const materialWritesSettled = (assetInstanceId: string): Promise<void> => editorWrites.idle([materialsOf(assetInstanceId)]);

const RESTORE_PATH = /^\/api\/v1\/deletions\/\{[A-Za-z]+\}\/restore$/;

export interface SceneWriteHandlers {
  /** Takes each scene write that fails while a scene is open. */
  failed?(failure: FailedWrite): void;
  /** Takes each scene write that succeeds while a scene is open, with what it sent and what it answered. */
  saved?(write: SceneWrite, body: unknown, answer: unknown): void;
  /** Where its scene writes in flight are noted; its own by default. */
  writes?: WritesInFlight;
}

/** The v1 client middleware that feeds every scene write to the recorder, and hands `failed` each one that fails while a scene is open. */
export function sceneWriteMiddleware(
  recorder: SceneWriteRecorder,
  { failed = () => undefined, saved = () => undefined, writes = writesInFlight() }: SceneWriteHandlers = {},
): Middleware {
  const pending = new Map<string, { write: SceneWrite; body: unknown; before: Before; gestureId?: string }>();
  const inFlight = new Map<string, () => void>();
  // Tracked writes in flight, oldest first: a write reads its rows' prior state only once the earlier writes
  // to any of them are recorded, so a quick second edit puts back what the first left, not what it replaced.
  // A delete's snapshot also reads the rows it takes along (keyframes, clips, materials, effects, targets),
  // which it has no key for, so it waits for every earlier write but other rows' deletes
  const tickets = new Map<string, Ticket>();
  // Every scene write in flight while a scene is open, tracked or not, and the rows each names
  const watched = new Map<string, Omit<FailedWrite, 'status' | 'settled'>>();
  // Every scene write and restore in flight while a scene is open, as `writes` counts them
  const begun = new Map<string, WriteInFlight>();
  const finish = (id: string) => {
    inFlight.get(id)?.();
    inFlight.delete(id);
    const own = tickets.get(id);
    tickets.delete(id);
    own?.setKeys([]);
    own?.setDone();
  };
  // Lets go of a write's rows, and hands a watched one on if it failed; a delete of a row already gone did what it meant to
  const settle = (id: string, failedWith: number | null) => {
    begun.get(id)?.end();
    begun.delete(id);
    const seen = watched.get(id);
    watched.delete(id);
    if (!seen) return;
    if (failedWith === null || (failedWith === 404 && seen.write.op === 'delete')) return;
    failed({ ...seen, status: failedWith, settled: () => writes.idle(seen.rows) });
  };
  const afterEarlierWrites = async (earlier: Ticket[], keys: string[], deletes: boolean) => {
    for (const other of earlier) {
      if (deletes && !other.deletes) {
        await other.done;
        continue;
      }
      const theirs = await other.keys;
      if (overlap(theirs, keys)) await other.done;
    }
  };
  return {
    async onRequest({ request, schemaPath, params, id }) {
      const isUntracked = request.headers.has(UNTRACKED_HEADER);
      const gestureId = request.headers.get(GESTURE_HEADER) ?? undefined;
      // Neither header is meant for the backend; the request is rebuilt without them only after its
      // body has been read, since rebuilding it takes the body
      const bare = () => (isUntracked || gestureId ? withoutScope(request) : undefined);
      const write = sceneWriteOf(request.method, schemaPath, Object.values(params.path ?? {})[0]);
      const sceneId = recorder.scene();
      if (!write) {
        // A restore names the rows it brings back only in its answer, so until then it may write any row
        if (sceneId && request.method === 'POST' && RESTORE_PATH.test(schemaPath)) begun.set(id, writes.begin());
        return bare();
      }
      const tracked = write.kind !== 'vfx' && !isUntracked && (!!gestureId || !recorder.gestureOnly(write));
      if (!tracked && !sceneId) return bare();
      // Noted before any await, so neither an undo nor the gesture gets ahead of this write while it is starting,
      // and its place among the writes in flight is taken
      if (sceneId) begun.set(id, writes.begin());
      const earlier = [...tickets.values()];
      const own = tracked ? ticket(write.op === 'delete') : null;
      if (own) {
        inFlight.set(id, recorder.writeStarted(gestureId));
        tickets.set(id, own);
      }
      try {
        const text = await request.clone().text();
        const body = text ? JSON.parse(text) : undefined;
        const keys = rowKeysOf(write, body, recorder.ports.material);
        if (sceneId) watched.set(id, { write, body, rows: keys, gestureId, byUser: tracked || !!gestureId, sceneId });
        // A delete may write any row, taking along rows it has no key for; a material write is also every material's, for a read whose asset is unknown
        if (write.op !== 'delete') begun.get(id)?.names(write.kind === 'material' && !keys.includes(ANY_MATERIAL) ? [...keys, ANY_MATERIAL] : keys);
        if (own) {
          own.setKeys(keys);
          await afterEarlierWrites(earlier, keys, write.op === 'delete');
          // A write whose prior state cannot be read goes ahead unrecorded, holding later writes to its rows and undo until it answers
          const before = await recorder.before(write, body).catch(() => null);
          if (before) pending.set(id, { write, body, before, gestureId });
        }
      } catch (error) {
        console.warn('[undo] a scene write could not be read before it was sent', error);
      }
      return bare();
    },
    async onResponse({ response, id }) {
      const entry = pending.get(id);
      pending.delete(id);
      const seen = watched.get(id);
      try {
        if ((!entry && !seen) || !response.ok) return undefined;
        const text = await response.clone().text();
        const answer = text ? JSON.parse(text) : null;
        if (entry) recorder.record(entry.write, entry.body, answer, entry.before, entry.gestureId);
        // Noted before the write lets go of its rows, so a read waiting for them compares with what it saved
        if (seen) saved(seen.write, seen.body, answer);
        return undefined;
      } finally {
        finish(id);
        settle(id, response.ok ? null : response.status);
      }
    },
    onError({ id }) {
      pending.delete(id);
      finish(id);
      settle(id, 0);
      return undefined;
    },
  };
}

function withoutScope(request: Request): Request {
  const headers = new Headers(request.headers);
  headers.delete(UNTRACKED_HEADER);
  headers.delete(GESTURE_HEADER);
  return new Request(request, { headers });
}
